import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { AppGlobals } from "src/app/app.globals";
import { WikiService } from "src/app/services/wiki.service";
import { SharedPipesModule } from "../../../pipes/shared.pipes.module";
import {
  DxButtonModule,
  DxScrollViewModule,
  DxTextBoxModule,
  DxToastModule,
} from "devextreme-angular";
import dxScrollView from "devextreme/ui/scroll_view";
import { environment } from "src/environments/environment";
import { BrowserModule } from "@angular/platform-browser";
import { ScrollableService } from "src/app/services/misc/scrollable.service";
import { SearchComponent } from "src/app/components/app-html-search/app-html-search.component";
import { AppConfig } from "src/app/app.config";
import { ActivityLogService } from "src/app/services/activity-log.service";
import { ActivityEntity, ActivitySection } from "src/app/constants/activity-log";

@Component({
    selector: "app-wiki-article",
    templateUrl: "./wiki-article.component.html",
    styleUrls: ["./wiki-article.component.css", "../wiki.css"],
    imports: [
        CommonModule,
        SharedPipesModule,
        DxButtonModule,
        DxScrollViewModule,
        DxToastModule,
        DxTextBoxModule,
        RouterLink,
        SearchComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None
})
export class WikiArticleComponent implements OnInit, AfterViewChecked {
  wikiSection = "internal";
  editPermission: boolean = false;

  toastVisible = false;

  searchValue: any;

  articleId: any;

  article: any;

  pageBreadCrumb: any = {
    text: $localize`Wiki/`,
    href: window.location.href,
    layer: 0,
  };

  imagePreview: any;
  imagePreviewVisible: boolean = false;

  @ViewChild("#wikiArticle", { static: false }) wikiArticle: ElementRef;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private appGlobals: AppGlobals,
    private wikiService: WikiService,
    private renderer: Renderer2,
    private el: ElementRef,
    private scrollableService: ScrollableService,
    private activityLog: ActivityLogService
  ) {
    this.editPermission = this.canEditWiki();
    this.wikiSection = this.router.url.split("/")[2];
  }

  @ViewChild("wikiscrollableDiv", { static: false }) scrollableDiv: ElementRef;
  hasInitialized = false;
  ngAfterViewChecked(): void {
    if (!this.hasInitialized && this.scrollableDiv) {
      this.scrollableService.setScrollableElement(this.scrollableDiv);
      this.hasInitialized = true;
    }
  }

  ngOnInit(): void {
    let params = this.activatedRoute.snapshot.params.id;

    const percentIndex = params.indexOf("#");
    this.articleId =
      percentIndex !== -1 ? params.substring(0, percentIndex) : params;

    if (this.activatedRoute.snapshot.paramMap.get("id")) {
      this.articleId = +this.activatedRoute.snapshot.paramMap.get("id");
    }

    let tagId = "";

    if (this.activatedRoute.snapshot.paramMap.get("tag")) {
      tagId = this.activatedRoute.snapshot.paramMap.get("tag");
    }

    if (this.articleId != 0) {
      this.wikiService
        .getWikiArticle({
          WikiArticleRequestItem: { Id: this.articleId },
          WikiSection: this.wikiSection,
        })
        .then(
          (result) => {
            if (result.hasError === true)
              AppGlobals.ShowErrorMessage(result.feedback);
            else {
              this.article = result.wikiArticleItem.article;
              this.activityLog.logView({
                Section: ActivitySection.WIKI,
                EntityType: ActivityEntity.WIKI_ARTICLE,
                EntityId: Number(this.articleId),
                EntityLabel: this.article?.title,
              });
              this.article.headers = result.wikiArticleItem.headers;
              this.groupFileBlocks();
              setTimeout(() => {
                if (tagId != "")
                  document.getElementById(tagId)?.scrollIntoView();

                let focusSectionElement = null;

                this.applyWikiHeadingStyle();

                if (focusSectionElement) {
                  let scrollContainer = this.el.nativeElement.querySelectorAll(
                    ".article-scroll-container"
                  );
                  if (scrollContainer.length != 0) {
                    let scrollPos = focusSectionElement.offsetTop;
                    scrollContainer[0].scrollTop = scrollPos;

                    focusSectionElement.className =
                      "article-section-heading-focussed";
                  }
                }
              }, 500);
            }
          },
          (error: any) => {
            AppGlobals.ShowErrorMessage(error);
          }
        );
    }
  }

  groupFileBlocks() {
    for (let pi = 0; pi < this.article.paragraphs.length; pi++) {
      for (let bi = 0; bi < this.article.paragraphs[pi].blocks.length; bi++) {
        const block = this.article.paragraphs[pi].blocks[bi];
        if (block.type == "image") {
          const prevBlock = this.article.paragraphs[pi].blocks[bi - 1];
          if (prevBlock.type == "image") {
            this.article.paragraphs[pi].blocks.splice(bi - 1, 2, {
              type: "file_group",
              files: [prevBlock.content, block.content],
              open: true,
            });
          } else if (prevBlock.type == "file_group") {
            this.article.paragraphs[pi].blocks.splice(bi, 1);
            prevBlock.files.push(block);
          }
        }
      }
    }
  }

  applyWikiHeadingStyle() {
    const articleElement =
      this.el.nativeElement.querySelectorAll(".article-container");

    articleElement.forEach((element) => {
      const hTags = element.querySelectorAll("h1, h2, h3, h4, h5");
      hTags.forEach((hTag) => {
        this.addWikiHeadingStyle(hTag);
        hTag.addEventListener("click", () =>
          this.copyToClipboard(`${this.wikiSection}:${hTag.id}`)
        );
      });
    });
  }

  // Needed as styling was not working with classes
  addWikiHeadingStyle(element: HTMLElement) {
    element.style.cursor = "pointer";
    element.style.width = "fit-content";
    element.style.transition = "text-decoration 0.3s ease";

    element.addEventListener("mouseenter", () => {
      element.style.textDecoration = "underline";
    });

    element.addEventListener("mouseleave", () => {
      element.style.textDecoration = "none";
    });
  }

  navigateToMainPage() {
    this.router.navigate(["/wiki/" + this.wikiSection]);
    AppGlobals.setBreadCrumbToLocalStorage(this.pageBreadCrumb);
  }

  copyToClipboard(text: string): void {
    const textarea = this.renderer.createElement("textarea");
    this.renderer.setProperty(textarea, "value", text);
    this.renderer.appendChild(this.el.nativeElement, textarea);

    textarea.select();
    document.execCommand("copy");

    this.renderer.removeChild(this.el.nativeElement, textarea);
    this.toastVisible = true;
  }

  searchValueChanged(e) {
    this.searchValue = e.value;
    const contentElement = document.getElementsByClassName("wiki-article")[0];

    this.removeHighlights(contentElement);

    if (this.searchValue) {
      this.highlightText(contentElement, this.searchValue);
    }
  }

  highlightText(element, searchTerm) {
    const regex = new RegExp(`(${searchTerm})`, "gi");

    // Traverse child nodes and highlight text
    for (let node of element.childNodes) {
      if (node.nodeType === 3) {
        // Text node
        const matches = node.nodeValue.match(regex);
        if (matches) {
          const highlightedText = node.nodeValue.replace(
            regex,
            "<mark>$1</mark>"
          );
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = highlightedText;
          while (tempDiv.firstChild) {
            element.insertBefore(tempDiv.firstChild, node);
          }
          element.removeChild(node);
        }
      } else if (node.nodeType === 1) {
        // Element node
        this.highlightText(node, searchTerm); // Recursive call
      }
    }
  }
  removeHighlights(element) {
    const marks = element.getElementsByTagName("mark");
    while (marks.length) {
      const parent = marks[0].parentNode;
      parent.replaceChild(marks[0].firstChild, marks[0]);
      parent.normalize(); // Combine adjacent text nodes
    }
  }

  applyFilter() {
    //   this.personGrid.instance.filter([this.searchFilter]);
  }

  goTo(path: string): void {
    this.router.navigate([path]);
    AppGlobals.setBreadCrumbToLocalStorage(this.pageBreadCrumb);
  }

  scrollToTop() {
    this.scrollableService.scrollTo(0);
  }

  scrollToElement(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  }

  canEditWiki(): boolean {
    if (this.wikiSection == "manual") {
      const email: string = AppGlobals.LoggedInUserData.email;
      if (
        !(
          (email.includes("@tdc.co.za") || email.includes("@gp-digital.io")) &&
          environment.production &&
          AppGlobals.TenantId == AppConfig.sharedWikiTenantId
        )
      ) {
        return false;
      }
    }
    return AppGlobals.UserPermissions.canEdit === true;
  }
}
